/**
 * RecipesRouter — hostname-aware /recipes dispatcher.
 *
 *  • hustleplate.au         → SalesRecipesPortal (static SEO portal,
 *                              browse-only, member CTA → hustlerecipes.au)
 *  • any tenant restaurant  → RecipeBookPage (their curated featured list)
 *  • ?sales=1 preview flag  → SalesRecipesPortal (preview escape hatch)
 */
import { useMemo } from "react";
import SalesRecipesPortal from "@/pages/sales/SalesRecipesPortal";
import SalesRecipeDetail from "@/pages/sales/SalesRecipeDetail";
import RecipeBookPage from "@/pages/RecipeBookPage";
import RecipeDetailPage from "@/pages/RecipeDetailPage";

const MARKETING_HOSTS = new Set([
  "hustleplate.au",
  "www.hustleplate.au",
]);
const STANDALONE_SALES = process.env.REACT_APP_STANDALONE_SALES === "1";

// Persist the sales-mode flag for the session so that internal links
// (e.g. clicking "Browse the library" from the salespage hero teaser)
// stay in the sales experience even though they don't carry ?sales=1.
const SALES_MODE_KEY = "hp_sales_mode";

function isSalesContext() {
  if (STANDALONE_SALES) return true;
  if (typeof window === "undefined") return false;
  const host = (window.location.hostname || "").toLowerCase();
  if (MARKETING_HOSTS.has(host)) return true;
  const params = new URLSearchParams(window.location.search);
  if (params.get("sales") === "1") {
    try { sessionStorage.setItem(SALES_MODE_KEY, "1"); } catch (_e) { /* ignore */ }
    return true;
  }
  // If the visitor *previously* landed with ?sales=1 in this tab, honour
  // sales mode for the rest of the session so internal navigation works.
  try {
    if (sessionStorage.getItem(SALES_MODE_KEY) === "1") return true;
  } catch (_e) { /* ignore */ }
  return false;
}

export function RecipesIndexRouter() {
  const showSales = useMemo(isSalesContext, []);
  return showSales ? <SalesRecipesPortal /> : <RecipeBookPage />;
}

export function RecipesDetailRouter() {
  const showSales = useMemo(isSalesContext, []);
  return showSales ? <SalesRecipeDetail /> : <RecipeDetailPage />;
}
